import Decimal from 'decimal.js';
// Isolate financial arithmetic from application changes to Decimal's global configuration.
// Four number inputs have at most 68 significant decimal digits in their string representations.
const QuantityDecimal = Decimal.clone({ precision: 80, rounding: Decimal.ROUND_DOWN });
function validateInstrument({ entityType, derivativeType }) {
    if (derivativeType === 'Future' && entityType === 'FuturesProduct')
        return;
    if (derivativeType === 'Option' && (entityType === 'Asset' || entityType === 'AssetIndex'))
        return;
    if (derivativeType === null && (entityType === 'Asset' || entityType === 'Cryptocurrency'))
        return;
    throw new Error(`Unsupported native quantity instrument: ${entityType}/${derivativeType ?? 'underlying'}`);
}
function validateFiniteNonnegative(value, name, allowZero) {
    if (!Number.isFinite(value) || value < 0 || (!allowZero && value === 0)) {
        throw new Error(`${name} must be finite and ${allowZero ? 'nonnegative' : 'positive'}`);
    }
}
function validateContractMultiplier(contractMultiplier, instrument) {
    validateFiniteNonnegative(contractMultiplier, 'Contract multiplier', false);
    const multiplier = new QuantityDecimal(contractMultiplier);
    if (multiplier.decimalPlaces() > 4 || multiplier.greaterThan('99999999.9999')) {
        throw new Error('Contract multiplier must fit positive DECIMAL(12,4)');
    }
    if (instrument.derivativeType === null && contractMultiplier !== 1) {
        throw new Error('Underlying instruments require a contract multiplier of 1');
    }
}
/** Derive a display/API unit from supported instrument identity, without storing redundant unit state. */
export function getQuantityUnit(instrument) {
    validateInstrument(instrument);
    if (instrument.derivativeType === 'Option' || instrument.derivativeType === 'Future')
        return 'contract';
    return instrument.entityType === 'Asset' ? 'share' : 'unit';
}
/**
 * Validate native shares, contracts, or crypto units within DECIMAL(38,18); derivative contracts must also be safe integers.
 * Zero is only permitted when explicitly requested for ledger state.
 * Uses the JS number's decimal representation, not arbitrary 38-digit decimal input precision.
 */
export function validateNativeQuantity(quantity, instrument, { allowZero = false } = {}) {
    validateInstrument(instrument);
    validateFiniteNonnegative(quantity, 'Native quantity', allowZero);
    if (instrument.derivativeType !== null && !Number.isSafeInteger(quantity)) {
        throw new Error('Native derivative quantity must be a whole safe number of contracts');
    }
    const decimalQuantity = new QuantityDecimal(quantity);
    if (decimalQuantity.decimalPlaces() > 18 || decimalQuantity.greaterThanOrEqualTo('1e20')) {
        throw new Error('Native quantity must fit nonnegative DECIMAL(38,18)');
    }
    return quantity === 0 ? 0 : quantity;
}
/** Exact native remainder for partial fills. Fully filled orders return zero; overfills are invalid. */
export function calculateRemainingNativeQuantity({ instrument, quantity, quantityFilled }) {
    validateNativeQuantity(quantity, instrument, { allowZero: true });
    validateNativeQuantity(quantityFilled, instrument, { allowZero: true });
    const remaining = new QuantityDecimal(quantity).minus(quantityFilled);
    const result = validateNativeQuantity(remaining.toNumber(), instrument, { allowZero: true });
    if (!remaining.equals(result)) {
        throw new Error('Remaining native quantity cannot be represented safely as a number');
    }
    return result;
}
/**
 * Deliberately round budget sizing down to a caller-supplied native increment. There is no default increment.
 * The result can be zero; callers must validate a positive native quantity before execution.
 * Supporting multiplier arithmetic does not authorize trading an instrument or multiplier.
 * Numeric inputs use their JS decimal representation; the result is not an arbitrary 38-digit decimal value.
 */
export function calculateNativeQuantityForBudget({ instrument, budget, price, contractMultiplier, quantityIncrement }) {
    if (instrument.entityType === 'FuturesProduct' && instrument.derivativeType === 'Future') {
        throw new Error('Futures reserve sizing requires explicit futures research economics, not quoted notional');
    }
    validateNativeQuantity(quantityIncrement, instrument);
    validateFiniteNonnegative(budget, 'Budget', true);
    validateFiniteNonnegative(price, 'Price', false);
    validateContractMultiplier(contractMultiplier, instrument);
    const unitValue = new QuantityDecimal(price).times(contractMultiplier);
    const incrementValue = unitValue.times(quantityIncrement);
    const roundedQuantity = new QuantityDecimal(budget).dividedToIntegerBy(incrementValue).times(quantityIncrement);
    const quantity = validateNativeQuantity(roundedQuantity.toNumber(), instrument, { allowZero: true });
    // Conversion back to a JS number must preserve the increment and may never exceed the budget.
    const result = new QuantityDecimal(quantity);
    if (!result.equals(roundedQuantity) || !result.modulo(quantityIncrement).isZero() || result.times(unitValue).greaterThan(budget)) {
        throw new Error('Rounded native quantity cannot be represented safely as a number');
    }
    return quantity;
}
/**
 * Unsigned value before fees. Zero prices support settlements; zero ledger quantities require allowZero.
 * Uses the JS numeric inputs' decimal representations and returns a number, not arbitrary decimal precision.
 */
export function calculateNativeGrossValue({ instrument, quantity, price, contractMultiplier, allowZero = false }) {
    if (instrument.entityType === 'FuturesProduct' && instrument.derivativeType === 'Future') {
        throw new Error('Futures cash valuation requires explicit price-change economics, not a principal payment');
    }
    validateNativeQuantity(quantity, instrument, { allowZero });
    validateFiniteNonnegative(price, 'Price', true);
    validateContractMultiplier(contractMultiplier, instrument);
    const value = new QuantityDecimal(quantity).times(price).times(contractMultiplier);
    const result = value.toNumber();
    if (!Number.isFinite(result) || (result === 0 && !value.isZero())) {
        throw new Error('Native gross value cannot be represented as a finite number');
    }
    return result === 0 ? 0 : result;
}
//# sourceMappingURL=nativeQuantity.js.map